import { useEffect, useRef, useState } from "react";
import { crLocalToISO, isoToCRInput } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import WallFeed from "@/components/WallFeed";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Megaphone, Upload, Trash2, Check, ImagePlus, Pencil } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const empty = { title: "", body: "", audience: "all", pinned: false, scheduled_for: "" };

export default function Wall() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [pending, setPending] = useState([]);
  const [caption, setCaption] = useState("");
  const imgRef = useRef(null);
  const photoRef = useRef(null);

  const loadPhotos = () => {
    api.get("/wall/photos").then((r) => setPhotos(r.data || []));
    api.get("/wall/photos", { params: { pending: true } }).then((r) => setPending(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadPhotos(); }, []);

  const publish = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Poné título y mensaje"); return; }
    setBusy(true);
    try {
      const r = await api.post("/wall/posts", { ...form, scheduled_for: form.scheduled_for || null });
      const post = Array.isArray(r.data) ? r.data[0] : r.data;
      const file = imgRef.current?.files?.[0];
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/wall/posts/${post.id}/image`, fd);
        imgRef.current.value = "";
      }
      setForm(empty);
      setReload((n) => n + 1);
      toast.success("Comunicado publicado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo publicar");
    } finally { setBusy(false); }
  };

  const uploadPhotos = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/wall/photos", fd, { params: caption ? { caption } : {} });
      }
      setCaption("");
      loadPhotos();
      toast.success(files.length > 1 ? "Fotos subidas" : "Foto subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir");
    } finally {
      setBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const editCaption = async (p) => {
    const next = window.prompt("Pie de foto", p.caption || "");
    if (next === null) return;
    await api.patch(`/wall/photos/${p.id}`, { caption: next });
    loadPhotos();
    toast.success("Pie de foto actualizado");
  };

  const approve = async (p) => { await api.patch(`/wall/photos/${p.id}`, { approved: true }); loadPhotos(); };
  const removePhoto = async (p) => {
    if (!window.confirm("¿Borrar esta foto?")) return;
    await api.delete(`/wall/photos/${p.id}`);
    loadPhotos();
  };

  return (
    <div>
      <PageHeader title="Muro de la comunidad" subtitle="Comunicados y fotografías para estudiantes y profesores" testId="wall-header" />

      <Card className="p-5 mb-6" data-testid="wall-composer">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone className="h-4 w-4 text-brand-blue" />
          <h2 className="text-sm font-bold text-slate-900">Nuevo comunicado</h2>
        </div>
        <div className="space-y-3">
          <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="wall-title-input" />
          <Textarea rows={4} placeholder="Mensaje para la comunidad" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="wall-body-input" />
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-56">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Para quién</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="wall-audience-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda la comunidad</SelectItem>
                  <SelectItem value="students">Solo estudiantes</SelectItem>
                  <SelectItem value="teachers">Solo profesores</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} data-testid="wall-pinned-switch" />
              <span className="text-sm text-slate-600">Fijar arriba</span>
            </div>
            <div className="w-56">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Programar (opcional)</Label>
              <Input
                type="datetime-local"
                value={isoToCRInput(form.scheduled_for)}
                onChange={(e) => setForm({ ...form, scheduled_for: crLocalToISO(e.target.value) })}
                data-testid="wall-schedule-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Hora de Costa Rica</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()} data-testid="wall-image-button">
                <ImagePlus className="h-4 w-4" /> Imagen
              </Button>
              <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="wall-image-input" />
            </div>
            <Button className="ml-auto" onClick={publish} disabled={busy} data-testid="wall-publish-button">Publicar</Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 mb-6" data-testid="photos-panel">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="h-4 w-4 text-orange-500" />
          <h2 className="text-sm font-bold text-slate-900">Fotografías de la semana</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Input className="max-w-xs" placeholder="Pie de foto (opcional)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="photo-caption-input" />
          <Button size="sm" variant="outline" onClick={() => photoRef.current?.click()} disabled={busy} data-testid="photo-upload-button">
            <Upload className="h-4 w-4" /> Subir fotos
          </Button>
          <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={uploadPhotos} data-testid="photo-upload-input" />
        </div>

        {pending.length > 0 && (
          <div className="mb-4" data-testid="photos-pending">
            <p className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold mb-2">
              Pendientes de autorización ({pending.length})
            </p>
            <div className="flex flex-wrap gap-3">
              {pending.map((p) => (
                <div key={p.id} className="relative w-32" data-testid={`pending-photo-${p.id}`}>
                  <img src={`${API_BASE}/api/wall/photos/${p.id}/image`} alt="" className="h-24 w-32 object-cover rounded-lg border border-amber-300" />
                  <p className="text-[10px] text-slate-500 truncate">{p.uploaded_by_name}</p>
                  <div className="flex gap-1 mt-1">
                    <Button size="sm" className="h-7 px-2" onClick={() => approve(p)} data-testid={`approve-photo-${p.id}`}>
                      <Check className="h-3 w-3" /> Autorizar
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removePhoto(p)} data-testid={`reject-photo-${p.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative w-32" data-testid={`published-photo-${p.id}`}>
              <img src={`${API_BASE}/api/wall/photos/${p.id}/image`} alt="" className="h-24 w-32 object-cover rounded-lg" />
              <p className="text-[10px] text-slate-500 truncate">{p.caption || "Sin pie de foto"}</p>
              <div className="absolute top-1 right-1 flex gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7 bg-white/90 hover:bg-white" onClick={() => editCaption(p)} data-testid={`edit-photo-${p.id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 bg-white/90 text-red-600 hover:bg-white" onClick={() => removePhoto(p)} data-testid={`delete-photo-${p.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
          {photos.length === 0 && <p className="text-sm text-slate-500">Todavía no hay fotos publicadas</p>}
        </div>
      </Card>

      <WallFeed key={reload} limit={20} />
    </div>
  );
}
